import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
/**
 * About App - Gereduceerde en logische animaties
 * Focus op subtiele interactie en een rustige achtergrond
 */
import { Sparkles, Zap, Users, ArrowRight, Star } from 'lucide-react';
import { useTheme } from '../../core/contexts/ThemeContext';
export function AboutApp() {
  const {
    getThemeClasses,
    theme: currentTheme
  } = useTheme();
  const theme = getThemeClasses();
  const [hoveredPillar, setHoveredPillar] = useState<string | null>(null);
  // [VERWIJDERD] mousePosition state is verwijderd
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isLightTheme = currentTheme === 'light';
  const isNeonTheme = currentTheme === 'neon';
  const isHighContrast = currentTheme === 'highContrast';

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // [VERWIJDERD] Enhanced mouse tracking effect is verwijderd

  const pillars = [{
    id: 'ai-video',
    icon: Sparkles,
    title: 'AI Video Generation',
    color: 'from-rose-500 to-pink-600',
    glowColor: 'rgba(244, 114, 182, 0.6)',
    description: 'Creative storytelling met AI',
    items: ['Gepersonaliseerde video-inhoud', 'Minimale input, maximale impact', 'Unieke verhalen vertellen'],
    emoji: '🎬'
  }, {
    id: 'vibe-coding',
    icon: Zap,
    title: 'Vibe Coding',
    color: 'from-emerald-500 to-teal-600',
    glowColor: 'rgba(20, 184, 166, 0.6)',
    description: 'AI-powered rapid prototyping',
    items: ['Snelle app development', 'Iteratief werken', 'Van idee naar werkend product'],
    emoji: '⚡'
  }, {
    id: 'ux-thinking',
    icon: Users,
    title: 'UX Thinking',
    color: 'from-purple-500 to-violet-600',
    glowColor: 'rgba(139, 92, 246, 0.6)',
    description: 'Gebruikerservaring als fundament',
    items: ['Empathisch ontwerpen', 'Intuïtieve interfaces', 'Focus op beleving'],
    emoji: '🎯'
  }];
  const journey = [{
    phase: '1',
    title: 'Ontdekken',
    description: 'Inzicht in de gebruiker en context',
    color: 'from-purple-500 to-violet-500',
    icon: '🔍'
  }, {
    phase: '2',
    title: 'Prototypen',
    description: 'Snel werkende concepten bouwen',
    color: 'from-blue-500 to-indigo-500',
    icon: '⚡'
  }, {
    phase: '3',
    title: 'Vertellen',
    description: 'Het verhaal visueel maken',
    color: 'from-pink-500 to-rose-500',
    icon: '✨'
  }];
  return <div ref={containerRef} className={`h-full overflow-y-auto relative ${isLightTheme ? 'bg-gradient-to-br from-white to-gray-100' : isHighContrast ? 'bg-black' : isNeonTheme ? 'bg-black' : 'bg-gradient-to-br from-gray-900 to-black'}`} data-id="element-8">
      {/* GEREDUCEERD: 
        - Eén grid-laag i.p.v. twee.
        - Geen 'Mega Floating Orbs' meer.
       */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-9">
        <motion.div animate={{
        backgroundPosition: ['0% 0%', '100% 100%']
      }} transition={{
        duration: 30,
        repeat: Infinity,
        ease: 'linear'
      }} className="w-full h-full" style={{
        backgroundImage: isHighContrast ? 'none' : `linear-gradient(${isNeonTheme ? '#00ffff' : isLightTheme ? '#94a3b8' : '#334155'} 1px, transparent 1px), 
                 linear-gradient(90deg, ${isNeonTheme ? '#00ffff' : isLightTheme ? '#94a3b8' : '#334155'} 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} data-id="element-10" />
      </div>

      {/* GEREDUCEERD:
        - Alleen nog de 'small particles' voor een subtiel sfeereffect.
        - Geen 'medium' of 'large' particles meer.
        - Aantal verminderd en animatie vertraagd.
       */}
      {!isHighContrast && <div className="absolute inset-0 pointer-events-none" data-id="element-11">
          {[...Array(isMobile ? 10 : 20)].map((_, i) => <motion.div key={`small-${i}`} className={`absolute rounded-full ${isNeonTheme ? i % 2 === 0 ? 'bg-cyan-400' : 'bg-fuchsia-400' : i % 2 === 0 ? 'bg-fuchsia-400' : 'bg-violet-400'}`} style={{
        width: 1 + Math.random() * 2,
        height: 1 + Math.random() * 2,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`
      }} animate={{
        y: [0, -100, 0],
        // Iets langere reis
        x: [0, (Math.random() - 0.5) * 40, 0],
        opacity: [0, 1, 0],
        scale: [0, 1.5, 0] // Subtielere schaal
      }} transition={{
        duration: 8 + Math.random() * 8,
        // Veel langzamer
        repeat: Infinity,
        delay: Math.random() * 5
      }} data-id="element-12" />)}
        </div>}
      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12 space-y-12 md:space-y-20" data-id="element-13">
        {/* HERO GEREDUCEERD:
          - Geen 'Rotating Rings', 'Energy Beams', 'Orbiting Stars' of 'Constellation Lines' meer.
          - Focus ligt op de avatar zelf.
          - Pulsing rings zijn verminderd en vertraagd.
         */}
        <motion.section initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center space-y-6 md:space-y-8" data-id="element-14">
          <div className="relative inline-block" data-id="element-15">
            {/* Main Avatar zonder muis-interactie */}
            <motion.div animate={{
            rotateY: 360
          }} transition={{
            rotateY: {
              duration: 15,
              // Vertraagd
              repeat: Infinity,
              ease: 'linear'
            }
          }} style={{
            transformStyle: 'preserve-3d',
            perspective: '1000px',
            // [VERWIJDERD] Muis-effect transformatie is verwijderd
            transform: 'none'
          }} className={`relative w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center ring-8 ${isNeonTheme ? 'ring-emerald-500/20 shadow-2xl shadow-emerald-500/50' : 'ring-emerald-500/10 shadow-2xl'}`} data-id="element-16">
              {/* Inner glow pulse verwijderd */}
              <motion.div animate={{
              // Rotate verwijderd, zat dubbelop
              scale: [1, 1.05, 1] // Subtielere pulse
            }} transition={{
              scale: {
                duration: 5,
                // Vertraagd
                repeat: Infinity
              }
            }} className="text-6xl md:text-7xl relative z-10" data-id="element-17">
                ✨
              </motion.div>
            </motion.div>

            {/* Pulsing Rings - Gereduceerd naar 3 en vertraagd */}
            {!isHighContrast && <>
                {[...Array(3)].map((_, i) => <motion.div key={i} initial={{
              scale: 1,
              opacity: 0.6
            }} animate={{
              scale: 3,
              opacity: 0
            }} transition={{
              duration: 5,
              // Vertraagd
              repeat: Infinity,
              delay: i * 1 // Meer spreiding
            }} className={`absolute inset-0 rounded-full border-2 ${isNeonTheme ? 'border-cyan-500' : 'border-emerald-500'}`} style={{
              boxShadow: isNeonTheme ? '0 0 20px rgba(0, 255, 255, 0.5)' : '0 0 20px rgba(16, 185, 129, 0.5)'
            }} data-id="element-18" />)}
              </>}
          </div>
          {/* Titel - Glitch effect verwijderd */}
          <div className="space-y-3 md:space-y-4" data-id="element-19">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className={`text-sm md:text-base font-medium ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-300' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-20">
              ✨ AI Creative System Thinker
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className={`text-4xl md:text-6xl font-bold leading-tight relative ${isHighContrast ? 'text-white' : ''}`} data-id="element-21">
              <span style={{
              background: isHighContrast ? '#ffffff' : isNeonTheme ? 'linear-gradient(135deg, #00ffff, #ff00ff, #ffff00)' : 'linear-gradient(135deg, #ec4899, #8b5cf6, #06b6d4)',
              backgroundSize: '200% 200%',
              WebkitBackgroundClip: isHighContrast ? 'unset' : 'text',
              WebkitTextFillColor: isHighContrast ? '#ffffff' : 'transparent',
              backgroundClip: isHighContrast ? 'unset' : 'text',
              textShadow: isNeonTheme ? '0 0 30px rgba(0, 255, 255, 0.5)' : undefined
            }} data-id="element-22">
                <motion.span animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
              }} transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'linear'
              }} style={{
                display: 'inline-block',
                background: isHighContrast ? '#ffffff' : isNeonTheme ? 'linear-gradient(135deg, #00ffff, #ff00ff, #ffff00)' : 'linear-gradient(135deg, #ec4899, #8b5cf6, #06b6d4)',
                backgroundSize: '200% 200%',
                WebkitBackgroundClip: isHighContrast ? 'unset' : 'text',
                WebkitTextFillColor: isHighContrast ? '#ffffff' : 'transparent',
                backgroundClip: isHighContrast ? 'unset' : 'text'
              }} data-id="element-23">
                  Hoi, ik ben Mick 👋
                </motion.span>
              </span>
              {/* Glitch effect overlay verwijderd */}
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className={`text-lg md:text-2xl max-w-3xl mx-auto ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-200' : isNeonTheme ? 'text-cyan-300' : 'text-white/80'}`} data-id="element-24">
              Ik vertaal complexe vragen naar elegante oplossingen door AI,
              design en ontwikkeling samen te brengen
            </motion.p>
          </div>
        </motion.section>
        {/* VERTALING SECTIE GEREDUCEERD:
          - Geen 'gradient overlays' meer binnen de kaart.
          - Geen 'floating sparkles' meer (was dubbelop met achtergrond).
          - Icon pulse verwijderd, alleen nog langzame rotatie.
          - Tekst-pulse verwijderd.
         */}
        <motion.section initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className={`backdrop-blur-xl rounded-3xl p-6 md:p-10 border relative overflow-hidden ${isLightTheme ? 'bg-white/80 border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-25">
          {/* Geen overlays meer */}
          {/* Geen sparkles meer */}
          <div className="relative z-10 space-y-6" data-id="element-26">
            <div className="flex items-center gap-3 mb-6" data-id="element-27">
              <motion.div animate={{
              rotate: [0, 360]
              // Scale pulse verwijderd
            }} transition={{
              rotate: {
                duration: 15,
                // Vertraagd
                repeat: Infinity,
                ease: 'linear'
              }
            }} className={`w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br ${isNeonTheme ? 'from-cyan-500 to-fuchsia-500' : 'from-fuchsia-500 to-violet-500'} flex items-center justify-center ${isNeonTheme ? 'shadow-lg shadow-fuchsia-500/50' : ''}`} data-id="element-28">
                <Sparkles className="w-6 h-6 md:w-7 md:h-7 text-white" data-id="element-29" />
              </motion.div>
              <h2 className={`text-2xl md:text-4xl font-bold ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-30">
                De Vertaalslag
              </h2>
            </div>
            <p className={`text-lg md:text-xl leading-relaxed ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/80'}`} data-id="element-31">
              Mijn werk draait om één kernvraag:{' '}
              <strong className={`${isHighContrast ? 'text-black underline' : isNeonTheme ? 'text-cyan-300' : 'text-emerald-400'}`} style={{
              display: 'inline-block'
            }} data-id="element-32">
                hoe maak ik het complexe simpel?
              </strong>
            </p>
            <p className={`text-base md:text-lg leading-relaxed ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/80'}`} data-id="element-33">
              Of het nu gaat om een ondernemer die een app-idee heeft maar niet
              weet waar te beginnen, of een organisatie die data wil
              visualiseren maar niet snapt hoe — ik vertaal die complexe
              vraagstukken naar concrete, werkende oplossingen die mensen
              snappen en willen gebruiken.
            </p>
          </div>
        </motion.section>
        {/* PIJLERS SECTIE GEREDUCEERD:
          - Dit was de grootste bron van 'hover-chaos'.
          - Geen muis-volgende translate meer op de hele kaart.
          - Geen 'Outer Glow Rings' op hover.
          - Geen 'Scan lines' op hover.
          - Geen 'Floating particles' *in* de kaart op hover.
          - Geen 'Pulse rings', 'glow ring' of 'inner particles' op het icoon.
          - Geen scale-pulse op de checkmarks.
          - BEHOUDEN: De 3D-rotatie van de kaart, de lift (scale), de gradient-verandering,
            de border-glow en de corner-accents. Dit geeft een rijke, maar
            beheerste hover-ervaring.
         */}
        <motion.section initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} data-id="element-34">
          <div className="text-center mb-8 md:mb-12" data-id="element-35">
            <h2 className={`text-2xl md:text-4xl font-bold mb-3 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-white' : 'text-white'}`} data-id="element-36">
              Drie Pijlers, Één Aanpak
            </h2>
            <p className={`text-base md:text-lg ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-300' : isNeonTheme ? 'text-cyan-300' : 'text-white/60'}`} data-id="element-37">
              Hoe ik waarde creëer
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8" data-id="element-38">
            {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            const isHovered = hoveredPillar === pillar.id;
            return <motion.div key={pillar.id} initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.7 + index * 0.1
            }} onHoverStart={() => !isMobile && setHoveredPillar(pillar.id)} onHoverEnd={() => !isMobile && setHoveredPillar(null)} onTouchStart={() => setHoveredPillar(pillar.id)} onTouchEnd={() => setTimeout(() => setHoveredPillar(null), 300)} className="relative group" style={{
              // [AANGEPAST] Muis-volg-effect verwijderd, vervangen door simpele scale
              transform: isHovered ? 'scale(1.05)' : 'scale(1)',
              transition: 'transform 0.3s ease-out'
            }} data-id="element-39">
                  {/* Outer glow rings verwijderd */}

                  {/* Kaart met 3D effect */}
                  <motion.div animate={{
                rotateY: !isMobile && isHovered ? 5 : 0,
                // Subtielere rotatie
                rotateX: !isMobile && isHovered ? -3 : 0
              }} style={{
                transformStyle: 'preserve-3d',
                perspective: '1000px'
              }} className={`relative backdrop-blur-xl rounded-2xl md:rounded-3xl p-6 md:p-8 border overflow-hidden ${isLightTheme ? 'bg-white/80 border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-40">
                    {/* Gradient overlays - animaties (scale/rotate) verwijderd, alleen opacity */}
                    {!isHighContrast && <>
                        <motion.div animate={{
                    opacity: isHovered ? 0.2 : 0.05
                  }} className={`absolute inset-0 bg-gradient-to-br ${pillar.color}`} style={{
                    mixBlendMode: 'screen'
                  }} data-id="element-41" />
                        <motion.div animate={{
                    opacity: isHovered ? 0.15 : 0.03
                  }} className={`absolute inset-0 bg-gradient-to-tl ${pillar.color}`} style={{
                    mixBlendMode: 'screen'
                  }} data-id="element-42" />
                      </>}
                    {/* Scan lines verwijderd */}
                    {/* Floating particles in card verwijderd */}

                    <div className="relative z-10" data-id="element-43">
                      {/* Icon - Alle pulses en rotaties verwijderd */}
                      <motion.div className="relative mb-6" data-id="element-44">
                        <div className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br ${pillar.color} flex items-center justify-center mx-auto relative ${isNeonTheme ? 'shadow-2xl shadow-fuchsia-500/50' : 'shadow-xl'}`} data-id="element-45">
                          <div className="text-3xl md:text-4xl relative z-10" data-id="element-46">
                            {pillar.emoji}
                          </div>
                          {/* Inner particles verwijderd */}
                        </div>
                        {/* Pulse rings verwijderd */}
                      </motion.div>

                      {/* Content - Simpele 'y' lift behouden */}
                      <motion.div animate={{
                    y: isHovered ? -10 : 0
                  }} data-id="element-47">
                        <h3 className={`text-xl md:text-2xl font-bold mb-3 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-48">
                          {pillar.title}
                        </h3>
                        <p className={`text-sm md:text-base mb-4 ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-300' : 'text-white/70'}`} data-id="element-49">
                          {pillar.description}
                        </p>
                        <ul className="space-y-2" data-id="element-50">
                          {pillar.items.map((item, i) => <motion.li key={item} initial={{
                        opacity: 0,
                        x: -10
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} transition={{
                        delay: 0.8 + index * 0.1 + i * 0.05
                      }} className={`text-sm flex items-start gap-2 ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/80'}`} data-id="element-51">
                              <span className={`mt-0.5 ${isHighContrast ? 'text-black' : isNeonTheme ? 'text-emerald-400' : 'text-emerald-500'}`} data-id="element-52">
                                ✓
                              </span>
                              <span data-id="element-53">{item}</span>
                            </motion.li>)}
                        </ul>
                      </motion.div>
                    </div>

                    {/* Corner accents - Scale-pulse verwijderd */}
                    {!isHighContrast && <>
                        {[{
                    pos: 'top-0 left-0',
                    angle: 135
                  }, {
                    pos: 'top-0 right-0',
                    angle: 45
                  }, {
                    pos: 'bottom-0 left-0',
                    angle: 225
                  }, {
                    pos: 'bottom-0 right-0',
                    angle: 315
                  }].map((corner, i) => <motion.div key={i} animate={{
                    opacity: isHovered ? 1 : 0
                  }} transition={{
                    duration: 0.3
                  }} className={`absolute ${corner.pos} w-8 h-8 md:w-12 md:h-12`} style={{
                    background: `linear-gradient(${corner.angle}deg, ${pillar.glowColor}, transparent)`
                  }} data-id="element-54" />)}
                      </>}

                    {/* Border glow - Opacity-pulse verwijderd */}
                    {!isHighContrast && isHovered && <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 0.6
                }} exit={{
                  opacity: 0
                }} className="absolute inset-0 rounded-2xl md:rounded-3xl" style={{
                  boxShadow: `inset 0 0 30px ${pillar.glowColor}, 0 0 30px ${pillar.glowColor}`
                }} data-id="element-55" />}
                  </motion.div>
                </motion.div>;
          })}
          </div>
        </motion.section>
        {/* JOURNEY SECTIE GEREDUCEERD:
          - Dit was al de rustigste sectie.
          - 'Animated background gradient' en 'floating particles' verwijderd.
          - 'Glow effect' op icon verwijderd.
          - Focus ligt nu puur op de SVG-lijnen en de simpele kaart-hover.
         */}
        <motion.section initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.9
      }} className={`backdrop-blur-xl rounded-3xl p-6 md:p-10 border relative overflow-hidden ${isLightTheme ? 'bg-white/80 border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-56">
          <h2 className={`text-2xl md:text-4xl font-bold mb-8 md:mb-12 text-center ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-57">
            Hoe Ze Samenwerken
          </h2>
          <div className="relative" data-id="element-58">
            {/* Connection Lines - Deze zijn prima, ze voegen context toe */}
            {!isMobile && !isHighContrast && <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-59">
                {/* Curved paths */}
                <motion.path d="M 20% 25% Q 50% 10%, 80% 25%" stroke={isNeonTheme ? '#00ffff' : '#ec4899'} strokeWidth="2" fill="none" strokeDasharray="5,5" initial={{
              pathLength: 0,
              opacity: 0
            }} animate={{
              pathLength: 1,
              opacity: 0.4
            }} transition={{
              duration: 2,
              delay: 1
            }} data-id="element-60" />
                <motion.path d="M 20% 50% Q 50% 65%, 80% 50%" stroke={isNeonTheme ? '#ff00ff' : '#8b5cf6'} strokeWidth="2" fill="none" strokeDasharray="5,5" initial={{
              pathLength: 0,
              opacity: 0
            }} animate={{
              pathLength: 1,
              opacity: 0.4
            }} transition={{
              duration: 2,
              delay: 1.2
            }} data-id="element-61" />
                <motion.path d="M 20% 75% Q 50% 60%, 80% 75%" stroke={isNeonTheme ? '#ffff00' : '#06b6d4'} strokeWidth="2" fill="none" strokeDasharray="5,5" initial={{
              pathLength: 0,
              opacity: 0
            }} animate={{
              pathLength: 1,
              opacity: 0.4
            }} transition={{
              duration: 2,
              delay: 1.4
            }} data-id="element-62" />
                {/* Animated particles along paths */}
                {[...Array(6)].map((_, i) => <motion.circle key={i} r="3" fill={isNeonTheme ? i % 2 === 0 ? '#00ffff' : '#ff00ff' : i % 2 === 0 ? '#ec4899' : '#8b5cf6'} initial={{
              offsetDistance: '0%',
              opacity: 0
            }} animate={{
              offsetDistance: '100%',
              opacity: [0, 1, 0]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              delay: i * 0.5
            }} style={{
              offsetPath: i % 3 === 0 ? 'path("M 20% 25% Q 50% 10%, 80% 25%")' : i % 3 === 1 ? 'path("M 20% 50% Q 50% 65%, 80% 50%")' : 'path("M 20% 75% Q 50% 60%, 80% 75%")'
            }} data-id="element-63" />)}
              </svg>}
            <div className="grid md:grid-cols-3 gap-6 md:gap-8 relative z-10" data-id="element-64">
              {journey.map((step, index) => <motion.div key={step.phase} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1 + index * 0.15
            }} whileHover={{
              y: -12,
              scale: 1.03
            }} className={`backdrop-blur-xl rounded-2xl p-6 border text-center relative overflow-hidden ${isLightTheme ? 'bg-gray-50 border-gray-200' : isHighContrast ? 'bg-gray-100 border-2 border-black' : isNeonTheme ? 'bg-fuchsia-900/20 border-fuchsia-500/30' : 'bg-white/5 border-white/10'}`} data-id="element-65">
                  {/* Animated background gradient verwijderd */}
                  {/* Floating particles verwijderd */}
                  <motion.div animate={{
                rotate: [0, 360]
              }} transition={{
                duration: 20,
                // Vertraagd
                repeat: Infinity,
                ease: 'linear'
              }} className={`w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center mx-auto mb-4 relative ${isNeonTheme ? 'shadow-lg shadow-fuchsia-500/50' : ''}`} data-id="element-66">
                    {/* Glow effect verwijderd */}
                    <div className="text-3xl md:text-4xl relative z-10" data-id="element-67">
                      {step.icon}
                    </div>
                  </motion.div>
                  <div className={`text-sm font-medium mb-2 ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-600' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-68">
                    Fase {step.phase}
                  </div>
                  <h3 className={`text-xl md:text-2xl font-bold mb-3 relative z-10 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-69">
                    {step.title}
                  </h3>
                  <p className={`text-sm md:text-base relative z-10 ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/80'}`} data-id="element-70">
                    {step.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </motion.section>
      </div>
    </div>;
}